import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@shared/components/ui/button';

interface ErrorFallbackProps {
  error: unknown;
  componentStack?: string;
  eventId?: string;
  resetError: () => void;
}

/**
 * Type guard to check if value is an Error object
 */
function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Type guard to check if value has a message property
 */
function hasMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}

/**
 * Extract error message with proper type handling
 * Supports Error objects, objects with message property, strings, and fallback for unknown types
 */
function getErrorMessage(error: unknown): string {
  if (isError(error)) {
    return error.message;
  }

  if (hasMessage(error)) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  // Fallback for unknown error types
  try {
    return JSON.stringify(error);
  } catch {
    return 'An unknown error occurred';
  }
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const errorMessage = getErrorMessage(error);

  const handleReload = () => {
    resetError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-red-100 dark:bg-red-950/30 p-3">
            <AlertTriangle className="size-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-2">
          Er is iets misgegaan
        </h1>

        <p className="text-muted-foreground mb-6">
          Er is een onverwachte fout opgetreden. Probeer de pagina opnieuw te laden.
        </p>

        {import.meta.env.DEV && errorMessage && (
          <div className="mb-6 p-4 bg-muted rounded-md text-left">
            <p className="text-xs font-mono text-muted-foreground break-all">
              {errorMessage}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button onClick={handleReload} size="lg" className="w-full">
            <RefreshCw className="size-4" />
            Pagina opnieuw laden
          </Button>

          <p className="text-sm text-muted-foreground">
            Het probleem blijft bestaan?{' '}
            <a
              href="mailto:support@lexlabour.nl"
              className="text-primary hover:underline font-medium"
            >
              Neem contact op met support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
